import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	ICartItem,
	IPrice,
	ITypeCartFields,
	OfferContext,
	VignetteContext,
	countryUrlMap,
	getCartEntriesSortedByModuleType,
	setRouteLangParam,
	getLabelValue,
	getUrlBasePath,
} from '@adac-mp/shared';
import {
	Badge,
	Button,
	CrossIcon,
	EmptyCartIcon,
	PriceInformation,
	ToggleGroup,
	Typography,
	ConfigNotStarted,
	ProductSummaryContainer,
	Modal,
	ProductConfiguration,
	ConfigDone,
	ChevronRight,
	HintMessage,
	getHintContent,
} from '../../index';
import { useHeaderNavbarOffset } from '../../hooks/useHeaderNavbarOffset';
import { useViewport } from '../../hooks/useViewport';
import { renderSectionTollProductMessage } from '../../utils/hintAndErrorMessages';
import { useFlowId } from '../../hooks/useFlowId';
interface ICartDrawerProps {
	reuse?: boolean;
	currStep?: number;
	totalSteps?: number;
	cartPrice?: IPrice;
	miniCartIsOpen: boolean;
	updatedCartTab?: string;
	setMiniCartIsOpen: (isOpen: boolean) => void;
	scrollToStep?: (step: number) => void;
}

const renderEmptyTabContent = (
	selectedTab: string,
	isConfigurePage: boolean,
	setCartTabSelected: (cartTabSelected: string) => void,
	cartLabels?: ITypeCartFields,
) => {
	const isConfigTab = selectedTab === 'Konfiguration';
	const renderTabTitle = (configTab: boolean, configPage: boolean) => {
		if (configTab)
			return configPage
				? cartLabels?.configurationNotStarted || 'Konfiguration noch nicht gestartet.'
				: cartLabels?.configurationCompleted || 'Konfiguration abgeschlossen.';
		return cartLabels?.cartEmpty || 'Momentan ist ihr Warenkorb leer.';
	};
	const renderTabIcon = (configTab: boolean, configPage: boolean) => {
		if (configTab) return configPage ? <ConfigNotStarted /> : <ConfigDone />;
		return <EmptyCartIcon />;
	};
	return (
		<div className="bg-gray-warm flex items-center justify-center h-[calc(100vh_-_121px)] sm:h-[calc(100vh_-_164px)]">
			<div className="flex flex-col items-center gap-2 max-w-[336px]">
				{renderTabIcon(isConfigTab, isConfigurePage)}
				<Typography className="text-center" variant="header" color="title" size="4xl">
					{renderTabTitle(isConfigTab, isConfigurePage)}
				</Typography>
				<Typography color="title" size="lgEven" className="text-center inline-flex pt-1">
					{isConfigurePage
						? cartLabels?.chooseProduct || 'Bitte wählen Sie zunächst ein Produkt.'
						: cartLabels?.productAdded || 'Das Produkt wurde Ihrem Warenkob hinzugefügt.'}
				</Typography>
				{!isConfigurePage && isConfigTab && (
					<Button
						className="bg-transparent"
						size="xl"
						variant="tertiary"
						onClick={() => setCartTabSelected('Warenkorb')}>
						{cartLabels?.gotToCartLabel || 'Zum Warenkorb'}
						<ChevronRight fill="currentColor" className="align-middle" />
					</Button>
				)}
			</div>
		</div>
	);
};

export const CartDrawer = ({
	reuse,
	currStep,
	totalSteps,
	cartPrice,
	miniCartIsOpen,
	updatedCartTab,
	setMiniCartIsOpen,
	scrollToStep = () => {},
}: ICartDrawerProps) => {
	const location = useLocation();
	const navigate = useNavigate();
	const flowId = useFlowId();
	const toggleGroupRef = useRef<HTMLDivElement | null>(null);
	const { selectedProducts, contentfulLabels, cartItems, deleteSelectedProducts } = useContext(VignetteContext);
	const { labels, lang, allOffers } = useContext(OfferContext);
	const cartLabels = contentfulLabels?.cart as ITypeCartFields;
	const getMiniCartTabs = () => {
		const tabs: { label: React.ReactNode; id: string }[] = [
			{
				label: (
					<span className="flex items-center justify-center gap-1.5">
						{cartLabels?.cart || 'Warenkorb'}{' '}
						{cartItems.length > 0 && <Badge className="rounded-half">{cartItems.length}</Badge>}
					</span>
				),
				id: 'Warenkorb',
			},
		];
		if (flowId !== 0) {
			tabs.unshift({ label: cartLabels?.configuration || 'Konfiguration', id: 'Konfiguration' });
		}
		return tabs;
	};
	const cartTabs = getMiniCartTabs();
	const { topOffset } = useHeaderNavbarOffset();
	const [cartTabSelected, setCartTabSelected] = useState<string>(cartTabs[0].id);
	const [openProductKey, setOpenProductKey] = useState<string | undefined>(undefined);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const { viewport } = useViewport();
	const isConfiguring = Number(currStep) > 1;
	const isConfigurePage = location.pathname.includes('configure');
	const countryUrl = countryUrlMap[flowId || 0];

	useEffect(() => {
		setCartTabSelected(cartTabs[0].id);
	}, [flowId]);

	useEffect(() => {
		if (updatedCartTab) {
			setCartTabSelected(updatedCartTab);
		}
	}, [updatedCartTab]);

	useEffect(() => {
		if (toggleGroupRef.current && miniCartIsOpen) {
			toggleGroupRef.current.focus();
		}
		if (miniCartIsOpen && viewport === 'mobile') {
			document.body.style.overflow = 'hidden';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [miniCartIsOpen, viewport]);

	const handleToggle = (currentKey?: string) => {
		setOpenProductKey(openProductKey !== currentKey ? currentKey : undefined);
	};

	const renderCartOverlappingMessage = (cartElements: ICartItem[]) =>
		cartElements.some(({ cartOverlapping }) => cartOverlapping) && (
			<HintMessage padding="large" hints={getHintContent('multipleVignettes', [], cartLabels?.hints)} />
		);

	const renderCartSummary = (totalPrice: IPrice, items: ICartItem[]) => {
		return (
			<>
				<div
					className="px-1.5 py-5 flex flex-col gap-5 overflow-scroll"
					style={{ height: `calc(100dvh - ${222 + topOffset}px)` }}>
					{getCartEntriesSortedByModuleType(items, allOffers).map(([moduleType, currCartItems]) => (
						<div key={`${moduleType}-section`} className="flex flex-col gap-3">
							<Typography className="inline-flex pl-4" color="title">
								{getLabelValue('mp.digital', labels) || 'Digitale'}{' '}
								{currCartItems[0].offer.moduleTypeId === 2
									? moduleType
									: getLabelValue('mp.tollSection', labels)}
							</Typography>
							{renderCartOverlappingMessage(currCartItems)}
							{renderSectionTollProductMessage(cartItems, cartLabels)}
							{currCartItems.map((product, index) => (
								<ProductSummaryContainer
									key={product.uuid}
									multipleProducts
									hasCategories
									hasSpecialPlates={countryUrl === 'oesterreich'} // To be discussed if we really need this
									cartItem={product}
									index={index}
									openProductKey={openProductKey}
									handleToggle={handleToggle}
								/>
							))}
						</div>
					))}
				</div>
				<div className="sticky bottom-0 left-0 right-0 bg-white p-1.5 flex flex-col gap-1.5 border-solid border-l-0 border-b-0 border-r-0 border-t border-t-gray-700">
					<PriceInformation
						simple
						className="px-4 py-1.5"
						totalPrice={{
							amount: `${totalPrice.amount}€`,
							decimalAmount: Number(totalPrice?.decimalAmount),
							label: cartLabels?.subtotal || 'Zwischensumme',
						}}
					/>
					<Button
						fullWidth
						data-cy="Mini Cart Button: go to checkout"
						tracking={{
							funnel_click: {
								label: cartLabels?.gotToCheckout || 'Zum Checkout',
								element: 'Warenkorb',
							},
						}}
						onClick={() => {
							if (isConfiguring) {
								setModalIsOpen(true);
								return;
							}
							navigate(getUrlBasePath(`/${countryUrl}/checkout${setRouteLangParam(lang)}`));
						}}>
						{cartLabels?.gotToCheckout || 'Zum Checkout'}
					</Button>
				</div>
			</>
		);
	};

	const renderMiniCartContent = (tabSelected: string) => {
		if (tabSelected === 'Warenkorb') {
			return cartItems.length > 0 && cartPrice
				? renderCartSummary(cartPrice, cartItems)
				: renderEmptyTabContent(tabSelected, isConfigurePage, setCartTabSelected, cartLabels);
		}
		return selectedProducts.length > 0 && isConfiguring && currStep && totalSteps ? (
			<ProductConfiguration
				reuse={reuse}
				currStep={currStep}
				totalSteps={totalSteps}
				scrollToStep={(step) => {
					if (miniCartIsOpen && viewport === 'mobile') {
						setMiniCartIsOpen(false);
						scrollToStep(step);
					} else {
						scrollToStep(step);
					}
				}}
			/>
		) : (
			renderEmptyTabContent(tabSelected, isConfigurePage, setCartTabSelected, cartLabels)
		);
	};

	return (
		<>
			<div
				className={`${
					miniCartIsOpen ? 'right-0' : '-right-full'
				} bg-gray-warm w-[calc(100%_-_24px)] fixed top-[56px] transition-[right] duration-300 z-50 bottom-0 shadow-innerShadow md:w-[516px]`}
				style={{ top: topOffset }}>
				<div className="flex border border-solid border-l-0 border-b-gray-700 border-r-0 border-t-0 bg-white">
					<ToggleGroup
						tabs
						noUnselect
						groupRef={toggleGroupRef}
						wrapperClassName="w-full flex"
						className={`flex-1 px-3.5 py-3.5 md:px-5 ${cartTabs.length <= 1 ? 'flex' : ''}`}
						value={cartTabSelected}
						options={cartTabs}
						setValue={(val) => {
							setCartTabSelected(val);
						}}
					/>
					<button
						className="cursor-pointer p-3.5 border-solid bg-transparent flex items-center justify-center border-l border-r-0 border-t-0 border-b-0 border-l-gray-700 group"
						onClick={() => setMiniCartIsOpen(false)}>
						<CrossIcon className="group-hover:[&>*]:stroke-gray-100" />
					</button>
				</div>
				{renderMiniCartContent(cartTabSelected)}
			</div>
			<Modal
				withSubmissionButtons
				title={cartLabels?.cancelConfigurtionTitle || 'Konfiguration abbrechen?'}
				submitButtonLabel={cartLabels?.gotToCheckout || 'Zum Checkout'}
				cancelButtonLabel={cartLabels?.cancelCancelConfiguration || 'Nein, Konfiguration fortfahren'}
				isOpen={modalIsOpen}
				onSubmit={() => {
					setModalIsOpen(false);
					deleteSelectedProducts();
					if (cartItems.length > 0) {
						navigate(getUrlBasePath(`/${countryUrl}/checkout${setRouteLangParam(lang)}`));
					}
				}}
				onClose={() => setModalIsOpen(false)}>
				<Typography component="p" className="pt-3 px-3 mb-3" size="lg" color="title">
					{cartLabels?.changesNotSaved || 'Ihre Änderungen werden nicht gespeichert.'}
				</Typography>
			</Modal>
		</>
	);
};
